// src/hooks/useProducts.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProductService } from '../admin/pages/product/productService';

export const PRODUCT_KEYS = {
  all:       ['products'] as const,
  lists:     () => ['products', 'list'] as const,
  paginated: (
    page: number,
    search: string,
    category: string,
    stock: string,
    variant: string,
    featured: string,
    brand: string,
  ) => ['products', 'list', page, search, category, stock, variant, featured, brand] as const,
  stats:     () => ['products', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface ProductPaginationParams {
  page: number;
  search: string;
  categoryFilter: string;
  stockFilter: string;
  variantFilter: string;
  featuredFilter: string;
  brandFilter: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getProductsPaginated instead of
// cursor objects. Any page can now be fetched in any order.
export const useProductsPaginated = (params: ProductPaginationParams) => {
  return useQuery({
    queryKey: PRODUCT_KEYS.paginated(
      params.page,
      params.search,
      params.categoryFilter,
      params.stockFilter,
      params.variantFilter,
      params.featuredFilter,
      params.brandFilter,
    ),
    queryFn: () =>
      ProductService.getProductsPaginated(
        params.pageSize,
        params.page,
        params.search        || undefined,
        params.categoryFilter !== 'all' ? params.categoryFilter : undefined,
        params.stockFilter    !== 'all' ? params.stockFilter    : undefined,
        params.variantFilter  !== 'all' ? params.variantFilter  : undefined,
        params.featuredFilter !== 'all' ? params.featuredFilter : undefined,
        params.brandFilter    !== 'all' ? params.brandFilter    : undefined,
      ),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev: any) => prev,
  });
};

export const useProductStats = () => {
  return useQuery({
    queryKey: PRODUCT_KEYS.stats(),
    queryFn: () => ProductService.getProductStats(),
    staleTime: 1000 * 60 * 3,
  });
};

export const useProductMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.all });
  };

  const remove = useMutation({
    mutationFn: (productId: string) => ProductService.deleteProduct(productId),
    onSuccess: invalidateAll,
  });

  const toggleFeatured = useMutation({
    mutationFn: ({ productId, isFeatured }: { productId: string; isFeatured: boolean }) =>
      ProductService.toggleFeaturedProduct(productId, isFeatured),
    onSuccess: invalidateAll,
  });

  return { remove, toggleFeatured };
};